import { SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';

import { FilingConductorDataModelCountResponse, FilingConductorIntegrationWebhookLogDetail, FilingConductorIntegrationWebhookLogResponse } from './types';

export interface UseFilingConductorIntegrationWebhookLogParams {
  rootFiCompanyId: number | null;
  /** Inclusive analysis-window bounds (ISO date). */
  fromDate: string | null;
  toDate: string | null;
  /** Defer the (potentially large) fetch until the modal is open. */
  enabled?: boolean;
}

function buildKey(rootFiCompanyId: number | null, fromDate: string | null, toDate: string | null, enabled: boolean, suffix: string) {
  const canFetch = enabled && rootFiCompanyId !== null && !!fromDate && !!toDate;
  if (!canFetch) return null;
  const query = new URLSearchParams({ fromDate: fromDate as string, toDate: toDate as string }).toString();
  return `filing-conductor/integrations/${rootFiCompanyId}/webhook-logs${suffix}?${query}`;
}

/** Total deduped IntegrationWebhookLog count for the card (no row payload). */
export function useFilingConductorIntegrationWebhookLogCount({ rootFiCompanyId, fromDate, toDate, enabled = true }: UseFilingConductorIntegrationWebhookLogParams) {
  const key = buildKey(rootFiCompanyId, fromDate, toDate, enabled, '/count');
  const { data, error, isLoading, mutate } = useSWR<FilingConductorDataModelCountResponse>(key, SWRFetcher({ method: 'GET' }));
  return { count: data?.count ?? null, isLoading, error, mutate };
}

/** Full deduped IntegrationWebhookLog row set; filters + pagination are applied client-side. */
export function useFilingConductorIntegrationWebhookLogs({ rootFiCompanyId, fromDate, toDate, enabled = true }: UseFilingConductorIntegrationWebhookLogParams) {
  const key = buildKey(rootFiCompanyId, fromDate, toDate, enabled, '');
  const { data, error, isLoading, mutate } = useSWR<FilingConductorIntegrationWebhookLogResponse>(key, SWRFetcher({ method: 'GET' }), { keepPreviousData: true });
  return { rows: data?.rows ?? [], totalCount: data?.totalCount ?? 0, isLoading, error, mutate };
}

/** Full IntegrationWebhookLog DB row (incl. payloadData) for the row-detail modal; fetched only when an id is set. */
export function useFilingConductorIntegrationWebhookLogDetail({ rootFiCompanyId, id }: { rootFiCompanyId: number | null; id: string | null }) {
  const key = rootFiCompanyId !== null && id ? `filing-conductor/integrations/${rootFiCompanyId}/webhook-logs/${id}` : null;
  const { data, error, isLoading } = useSWR<FilingConductorIntegrationWebhookLogDetail>(key, SWRFetcher({ method: 'GET' }));
  return { detail: data ?? null, isLoading, error };
}
